import type { FC, ReactNode } from 'react';
import { createContext, useContext, useMemo } from 'react';

export interface ArtifactLookupEntry {
  type: string;
  externalId: string;
  title?: string | null;
  status?: string | null;
  url?: string | null;
}

export interface ArtifactLookupContextValue {
  projectKey: string;
  lookup: (type: string, externalId: string) => ArtifactLookupEntry | undefined;
}

const ArtifactLookupContext = createContext<ArtifactLookupContextValue | null>(null);

export const useArtifactLookup = (): ArtifactLookupContextValue | null => useContext(ArtifactLookupContext);

export interface ArtifactLookupProviderProps {
  projectKey: string;
  artifacts: ArtifactLookupEntry[];
  // canonicalize is applied at both index time and on each lookup key so an inline
  // token with a slightly non-canonical externalId (e.g. trailing-slash PR URL) still
  // resolves. Defaults to identity if omitted.
  canonicalize?: (type: string, externalId: string) => string;
  children: ReactNode;
}

const identity = (_type: string, externalId: string) => externalId;

export const ArtifactLookupProvider: FC<ArtifactLookupProviderProps> = ({
  projectKey,
  artifacts,
  canonicalize = identity,
  children,
}) => {
  const value = useMemo<ArtifactLookupContextValue>(() => {
    const index = new Map<string, ArtifactLookupEntry>();
    for (const entry of artifacts) {
      const key = `${entry.type} ${canonicalize(entry.type, entry.externalId)}`;
      index.set(key, entry);
    }
    return {
      projectKey,
      lookup: (type, externalId) => index.get(`${type} ${canonicalize(type, externalId)}`),
    };
  }, [projectKey, artifacts, canonicalize]);

  return <ArtifactLookupContext.Provider value={value}>{children}</ArtifactLookupContext.Provider>;
};
